import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Check } from "lucide-react";

/**
 * Reusable upload button.
 * Files larger than CHUNK_THRESHOLD bytes are uploaded in 5MB chunks via
 * /api/upload/init, /api/upload/chunk, /api/upload/finalize so they bypass
 * the production ingress request-size limit. Smaller files still use the
 * single-shot /api/upload endpoint.
 *
 * Props:
 *   - accept: MIME filter string
 *   - label: text shown on the button
 *   - currentUrl: existing URL to show as "uploaded"
 *   - onUploaded: ({url, filename, size, content_type}) => void
 *   - testid: data-testid root
 *   - kind: "video" | "image" | "file" (cosmetic)
 *   - uploadAs: optional "as" query param (e.g. "thumbnail")
 */
const MAX_GB = 2;
const MAX_BYTES = MAX_GB * 1024 * 1024 * 1024;
const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB
const CHUNK_THRESHOLD = 25 * 1024 * 1024; // > 25 MB → chunked

export default function FileUpload({ accept = "video/*,image/*,application/pdf", label = "Upload", currentUrl, onUploaded, testid = "upload", kind = "file", uploadAs }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const pick = () => inputRef.current?.click();

  const singleShotUpload = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const r = await api.post("/upload", form, {
      params: uploadAs ? { as: uploadAs } : undefined,
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 30 * 60 * 1000,
      onUploadProgress: (evt) => {
        if (evt.total) setProgress(Math.round(100 * evt.loaded / evt.total));
      },
    });
    return r.data;
  };

  const chunkedUpload = async (file) => {
    // 1. init
    const initRes = await api.post("/upload/init", {
      filename: file.name,
      size: file.size,
      content_type: file.type || "application/octet-stream",
    });
    const uploadId = initRes.data.upload_id;
    const chunkSize = initRes.data.chunk_size || CHUNK_SIZE;
    const totalChunks = Math.ceil(file.size / chunkSize);

    // 2. upload chunks sequentially (sequential keeps memory low + plays nicer with proxies)
    let sent = 0;
    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const blob = file.slice(start, end);
      const form = new FormData();
      form.append("upload_id", uploadId);
      form.append("index", String(i));
      form.append("chunk", blob, `chunk_${i}`);
      // simple retry: 2 tries per chunk
      let attempt = 0;
      while (true) {
        try {
          await api.post("/upload/chunk", form, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 5 * 60 * 1000,
          });
          break;
        } catch (err) {
          attempt += 1;
          if (attempt >= 3) throw err;
          await new Promise((r) => setTimeout(r, 800 * attempt));
        }
      }
      sent = end;
      setProgress(Math.round((sent / file.size) * 98)); // leave 2% for finalize
    }

    // 3. finalize
    const finRes = await api.post("/upload/finalize", {
      upload_id: uploadId,
      total_chunks: totalChunks,
      ...(uploadAs ? { as: uploadAs } : {}),
    }, { timeout: 10 * 60 * 1000 });
    setProgress(100);
    return finRes.data;
  };

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`File is ${(file.size / 1024 / 1024 / 1024).toFixed(1)}GB. Max is ${MAX_GB}GB. Compress to 720p H.264 first.`);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const useChunked = file.size > CHUNK_THRESHOLD;
      const data = useChunked ? await chunkedUpload(file) : await singleShotUpload(file);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const fullUrl = `${backendUrl}${data.url}`;
      onUploaded?.({ ...data, url: fullUrl });
      const sizeLabel = file.size > 1024 * 1024 * 1024
        ? `${(file.size / 1024 / 1024 / 1024).toFixed(2)} GB`
        : `${(file.size / 1024 / 1024).toFixed(1)} MB`;
      toast.success(`Uploaded · ${sizeLabel}${useChunked ? " (chunked)" : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = () => onUploaded?.({ url: "", filename: "", size: 0, content_type: "" });

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`${testid}-wrap`}>
      <input ref={inputRef} type="file" accept={accept} onChange={onChange} className="hidden" data-testid={`${testid}-input`} />
      <button
        type="button"
        onClick={pick}
        disabled={uploading}
        data-testid={`${testid}-button`}
        className="btn-secondary text-xs"
      >
        <Upload size={12} strokeWidth={1.5} />
        {uploading ? `Uploading… ${progress}%` : label}
      </button>
      {currentUrl && !uploading && (
        <>
          <span className="overline text-[#166534] flex items-center gap-1" data-testid={`${testid}-status`}>
            <Check size={12} strokeWidth={2} /> {kind === "video" ? "video uploaded" : kind === "image" ? "image uploaded" : "file uploaded"}
          </span>
          <button type="button" onClick={clear} className="text-xs text-[#52525B] hover:text-[#991B1B]" title="Remove">
            <X size={12} />
          </button>
        </>
      )}
    </div>
  );
}
